import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationAdapter from 'lumen/adapters/application';
import config from 'lumen/config/environment';
import { Promise as RsvpPromise } from 'rsvp';

interface AggregatedUserSeed {
  bi_benchmarking_property_count: number;
  email: string;
  first_name: string;
  id: number;
  last_active: string | null;
  last_name: string;
  rate_insight_property_count: number;
}

const SEED_USERS: AggregatedUserSeed[] = [
  {
    id: 325667,
    first_name: 'Alex',
    last_name: 'Johnson',
    email: 'alex.johnson@hotel.com',
    last_active: '2026-02-16T10:30:00Z',
    rate_insight_property_count: 2,
    bi_benchmarking_property_count: 1,
  },
  {
    id: 2,
    first_name: 'Iva',
    last_name: 'Ryan',
    email: 'iva.ryan@hotel.com',
    last_active: '2026-01-29T14:15:00Z',
    rate_insight_property_count: 1,
    bi_benchmarking_property_count: 0,
  },
  {
    id: 3,
    first_name: 'Charlie',
    last_name: 'Brown',
    email: 'charlie.brown@hotel.com',
    last_active: null,
    rate_insight_property_count: 0,
    bi_benchmarking_property_count: 2,
  },
  {
    id: 4,
    first_name: 'Diana',
    last_name: 'Prince',
    email: 'diana.prince@hotel.com',
    last_active: '2026-04-01T16:20:00Z',
    rate_insight_property_count: 3,
    bi_benchmarking_property_count: 2,
  },
];

type StringSeedField = 'email' | 'first_name' | 'last_name';

const SORT_FIELD_MAP: Record<string, StringSeedField> = {
  name: 'first_name',
};

export default class AggregatedUserAdapter extends ApplicationAdapter {
  private buildMockQueryResponse(
    query: Record<string, unknown>,
  ): Record<string, unknown> {
    const page = Number(query['page'] ?? 1);
    const pageSize = Number(query['page_size'] ?? 100);
    const sortBy = String((query['sort_by'] as string | undefined) ?? 'name');
    const sortDirection = String(
      (query['sort_direction'] as string | undefined) ?? 'asc',
    );
    const search = query['search'] as string | undefined;
    const product = query['product'] as string | undefined;

    let filtered = [...SEED_USERS];

    if (search) {
      const terms = search.split(',').map((t) => t.trim().toLowerCase());
      filtered = filtered.filter((u) =>
        terms.some(
          (term) =>
            u.first_name.toLowerCase().includes(term) ||
            u.last_name.toLowerCase().includes(term) ||
            u.email.toLowerCase().includes(term),
        ),
      );
    }

    if (product === 'rate_insight_market_insight') {
      filtered = filtered.filter((u) => u.rate_insight_property_count > 0);
    } else if (product === 'bi_benchmark') {
      filtered = filtered.filter((u) => u.bi_benchmarking_property_count > 0);
    }

    const sortField = SORT_FIELD_MAP[sortBy] ?? 'first_name';
    const sortedUsers = filtered.sort((a, b) => {
      const aVal = a[sortField] ?? '';
      const bVal = b[sortField] ?? '';
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'desc' ? -comparison : comparison;
    });

    const start = (page - 1) * pageSize;
    const paginatedUsers = sortedUsers.slice(start, start + pageSize);

    return {
      users: paginatedUsers,
      meta: {
        page,
        page_size: pageSize,
        total_items: sortedUsers.length,
        total_pages: Math.ceil(sortedUsers.length / pageSize),
      },
    };
  }

  query(
    store: Store,
    type: ModelSchema,
    query: Record<string, unknown>,
  ): RsvpPromise<unknown> {
    if (!config.isStagingOrDevelopment) {
      return super.query(store, type, query);
    }

    return RsvpPromise.resolve(this.buildMockQueryResponse(query));
  }
}
